/**
 * Confeti sin dependencias para el festejo de matrícula: papelitos en naranja WAPP, oro, crema y tinta que salen de los dos
 * costados y caen con giro. Se dibuja en un <canvas> a pantalla completa y se apaga solo cuando ya no queda ninguno.
 * Respeta "reducir movimiento" del sistema: sin animación.
 */
const COLORES = ["#F45030", "#FF7A50", "#E0A93C", "#A8730A", "#FFF4E8", "#2B1C16"];

interface Papel { x: number; y: number; vx: number; vy: number; g: number; rot: number; vr: number; w: number; h: number; c: string; forma: number; vida: number; }

export const sinMovimiento = (): boolean => {
	try { return window.matchMedia("(prefers-reduced-motion: reduce)").matches; } catch { return false; }
};

export function lanzarConfeti(canvas: HTMLCanvasElement, rafagas = 3): () => void {
	const ctx = canvas.getContext("2d");
	if (!ctx || sinMovimiento()) return () => undefined;
	const dpr = Math.min(2, window.devicePixelRatio || 1);
	const medir = () => {
		canvas.width = window.innerWidth * dpr;
		canvas.height = window.innerHeight * dpr;
		ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
	};
	medir();
	window.addEventListener("resize", medir);
	const W = () => window.innerWidth;
	const H = () => window.innerHeight;
	const papeles: Papel[] = [];
	// En el celular, menos papelitos: se ve igual de lleno y no se traba.
	const porRafaga = W() < 700 ? 70 : 130;
	const rafaga = (desdeIzq: boolean) => {
		for (let i = 0; i < porRafaga; i++) {
			const ang = (desdeIzq ? -60 : -120) + (Math.random() - 0.5) * 50;
			const vel = 9 + Math.random() * 11;
			papeles.push({
				x: desdeIzq ? -10 : W() + 10, y: H() * (0.55 + Math.random() * 0.25),
				vx: Math.cos((ang * Math.PI) / 180) * vel, vy: Math.sin((ang * Math.PI) / 180) * vel,
				g: 0.22 + Math.random() * 0.12, rot: Math.random() * 360, vr: (Math.random() - 0.5) * 18,
				w: 6 + Math.random() * 6, h: 9 + Math.random() * 8, c: COLORES[(Math.random() * COLORES.length) | 0],
				forma: Math.random() < 0.25 ? 1 : 0, vida: 0,
			});
		}
	};
	const lluvia = () => {
		for (let i = 0; i < porRafaga; i++) {
			papeles.push({
				x: Math.random() * W(), y: -20 - Math.random() * H() * 0.4, vx: (Math.random() - 0.5) * 2, vy: 2 + Math.random() * 3,
				g: 0.05 + Math.random() * 0.05, rot: Math.random() * 360, vr: (Math.random() - 0.5) * 14,
				w: 6 + Math.random() * 6, h: 9 + Math.random() * 8, c: COLORES[(Math.random() * COLORES.length) | 0],
				forma: Math.random() < 0.25 ? 1 : 0, vida: 0,
			});
		}
	};
	const tiempos: any[] = [];
	rafaga(true);
	rafaga(false);
	for (let i = 1; i < rafagas; i++) tiempos.push(setTimeout(() => (i % 2 ? lluvia() : (rafaga(true), rafaga(false))), i * 650));

	let raf = 0;
	const paso = () => {
		ctx.clearRect(0, 0, W(), H());
		for (let i = papeles.length - 1; i >= 0; i--) {
			const p = papeles[i];
			p.vida++;
			p.vx *= 0.985;
			p.vy = Math.min(p.vy + p.g, 7);
			p.x += p.vx + Math.sin(p.vida / 12) * 0.6;
			p.y += p.vy;
			p.rot += p.vr;
			if (p.y > H() + 30) { papeles.splice(i, 1); continue; }
			ctx.save();
			ctx.translate(p.x, p.y);
			ctx.rotate((p.rot * Math.PI) / 180);
			// El "giro" en 3D: se aplasta a lo ancho según el ángulo.
			ctx.scale(Math.cos((p.rot * Math.PI) / 90), 1);
			ctx.fillStyle = p.c;
			if (p.forma) { ctx.beginPath(); ctx.arc(0, 0, p.w / 2, 0, Math.PI * 2); ctx.fill(); } else ctx.fillRect(-p.w / 2, -p.h / 2, p.w, p.h);
			ctx.restore();
		}
		if (papeles.length || tiempos.length) raf = requestAnimationFrame(paso);
	};
	raf = requestAnimationFrame(paso);
	// Las ráfagas programadas ya salieron: dejar que el bucle termine cuando caiga el último.
	tiempos.push(setTimeout(() => { tiempos.length = 0; }, rafagas * 650 + 50));
	return () => {
		cancelAnimationFrame(raf);
		tiempos.forEach(clearTimeout);
		window.removeEventListener("resize", medir);
		ctx.clearRect(0, 0, W(), H());
	};
}

/**
 * Sonido del festejo (Esteban, 2026-10-14: "lo más cercano posible" al "Success Sound Effect" de YouTube que mandó).
 * Medido de ese video, no copiado: barrido de campanitas que sube en ~0.33 s por un acorde la-re-mi (la4 → re7) y se queda
 * vibrando ~3 s. Cada nota: seno con su octava, dos voces apenas desafinadas (el "brillo" que late) y un destello agudo al
 * golpe. Web Audio, sin archivo. El navegador solo lo deja sonar después de que la persona tocó la página.
 */
const BARRIDO = [440, 587.33, 659.26, 880, 1174.66, 1318.51, 1760, 2349.32];
const PASO_S = 0.04;
// Ajustado contra la curva de volumen medida del video (cada 40 ms): sube en ~0.3 s, cae rápido a 1/4 y luego se apaga lento.
const CUERPO_S = 0.3;
const NIVEL_CUERPO = 0.25;
const DURA_S = 6;
const CORTE_S = 3.0;

export function sonarFestejo(): void {
	try {
		const AC: any = (window as any).AudioContext || (window as any).webkitAudioContext;
		if (!AC) return;
		const ac = new AC();
		const t0 = ac.currentTime + 0.03;
		const salida = ac.createGain();
		salida.gain.setValueAtTime(0.28, t0);
		salida.gain.setValueAtTime(0.28, t0 + CORTE_S);
		salida.gain.linearRampToValueAtTime(0, t0 + CORTE_S + 0.45);   // como el video: se corta suave a los ~3 s
		salida.connect(ac.destination);
		// Cola de sala: un eco corto y suave.
		const eco = ac.createDelay(1);
		const vuelta = ac.createGain();
		const mojado = ac.createGain();
		eco.delayTime.value = 0.13;
		vuelta.gain.value = 0.3;
		mojado.gain.value = 0.22;
		eco.connect(vuelta).connect(eco);
		eco.connect(mojado).connect(salida);
		const voz = (f: number, ini: number, vol: number, cuerpo: number, nivel: number, dura: number) => {
			const o = ac.createOscillator();
			const g = ac.createGain();
			o.type = "sine";
			o.frequency.value = f;
			g.gain.setValueAtTime(0, ini);
			g.gain.linearRampToValueAtTime(vol, ini + 0.004);
			g.gain.exponentialRampToValueAtTime(vol * nivel, ini + cuerpo);
			g.gain.exponentialRampToValueAtTime(0.0001, ini + dura);
			o.connect(g);
			g.connect(salida);
			g.connect(eco);
			o.start(ini);
			o.stop(Math.min(ini + dura, t0 + CORTE_S + 0.5));
		};
		BARRIDO.forEach((f, i) => {
			const ini = t0 + i * PASO_S;
			const vol = 0.26 + i * 0.006;
			voz(f * 1.0023, ini, vol * 0.55, CUERPO_S, NIVEL_CUERPO, DURA_S);   // dos voces a ±4 centésimas: el latido del brillo
			voz(f / 1.0023, ini, vol * 0.55, CUERPO_S, NIVEL_CUERPO, DURA_S);
			voz(f * 2, ini, vol * 0.28, CUERPO_S * 0.6, NIVEL_CUERPO, DURA_S / 2);   // octava
			if (f * 4 < 9000) voz(f * 4, ini, vol * 0.12, 0.05, 0.25, 0.09);   // destello del golpe
		});
		setTimeout(() => ac.close?.(), (CORTE_S + 1) * 1000);
	} catch { /* sin sonido */ }
}
